/**
 * Pure helpers behind WorldScene (owner: env-builder): spawn resolution, exit target → scene key,
 * the fallback map, the camera-bounds policy and the weather → NPC hand-off. No Phaser import, so
 * every rule is unit-tested in Node (tests/unit/world.scene-helpers.test.ts).
 */
import { TILE } from '@/core/config';
import { SceneKeys, type SceneKey } from '@/core/scenes/keys';
import type { Facing, SceneEntryData, WeatherState } from '@/core/types';
import type { MapData } from '@/core/types/contracts';
import type { WeatherDirector, WeatherEmission } from './WeatherDirector';

export interface SpawnPoint { x: number; y: number; facing: Facing }
export interface CameraBounds { x: number; y: number; w: number; h: number }
/** Anything that runs NPC schedules and can be told which weather state to run them for. */
export interface WeatherFollower { setWeather(state: WeatherState): void }

/** 20 × 12 open field with a solid border, used when a map id is unknown; the scene keeps running. */
export function fallbackMap(id: string): MapData {
  const width = 20, height = 12;
  const collision = new Array<number>(width * height).fill(0);
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) if (x === 0 || y === 0 || x === width - 1 || y === height - 1) collision[y * width + x] = 1;
  return {
    id, width, height, tileSize: TILE, layers: [], collision, exits: [],
    spawns: [{ id: 'start', x: (width / 2) * TILE, y: (height / 2) * TILE, facing: 'down' }],
    interactions: [], npcs: [],
  };
}

/**
 * Spawn precedence: `entry.position` (only when it was saved on this map) > `entry.spawn` >
 * `defaultSpawn` > the map's `start` spawn > its first spawn > the map centre. A named spawn that
 * the map lacks is warned about and skipped. Coordinates are rounded to integers.
 */
export function resolveSpawn(
  entry: SceneEntryData, mapId: string, map: MapData, defaultSpawn?: string,
  warn: (message: string) => void = (m) => console.warn(m),
): SpawnPoint {
  const pos = entry.position;
  if (pos && pos.map === mapId) return { x: Math.round(pos.x), y: Math.round(pos.y), facing: pos.facing };
  for (const name of [entry.spawn, defaultSpawn]) {
    if (!name) continue;
    const found = map.spawns.find((s) => s.id === name);
    if (found) return { x: Math.round(found.x), y: Math.round(found.y), facing: found.facing };
    warn(`[world] spawn "${name}" not found in map ${mapId}; using the next fallback`);
  }
  const first = map.spawns.find((s) => s.id === 'start') ?? map.spawns[0];
  if (first) return { x: Math.round(first.x), y: Math.round(first.y), facing: first.facing };
  return { x: Math.round((map.width * map.tileSize) / 2), y: Math.round((map.height * map.tileSize) / 2), facing: 'down' };
}

/**
 * Maps an exit's `target` to a scene key: an exact `SceneKeys` value, or a map id such as
 * `green-harbor` → `GreenHarbor` (separators dropped, case-insensitive). Null when nothing matches.
 */
export function sceneKeyForExit(target: string): SceneKey | null {
  const keys = Object.values(SceneKeys) as SceneKey[];
  const exact = keys.find((k) => k === target);
  if (exact) return exact;
  const norm = target.replace(/[-_\s]/g, '').toLowerCase();
  return keys.find((k) => k.toLowerCase() === norm) ?? null;
}

/**
 * Phaser camera bounds for a map. Inside bounds Phaser clamps scroll to >= 0, which would pin a map
 * smaller than the view to the top-left corner; WorldScene centres such maps itself (`clampScroll`
 * returns a negative scroll), so bounds are installed only when the map covers the view in both
 * dimensions. Null = leave the camera unbounded.
 */
export function cameraBoundsFor(mapW: number, mapH: number, viewW: number, viewH: number): CameraBounds | null {
  if (mapW < viewW || mapH < viewH) return null;
  return { x: 0, y: 0, w: mapW, h: mapH };
}

/**
 * Single owner of the weather → NPC hand-off. NPC schedules switch to `weatherBehaviour[target]` the
 * moment a transition *starts* (the `t = 0` emission), not when it settles — people run for shelter
 * when the first drops fall, and a mid-transition redirect re-targets them at once. The characters
 * `NpcScheduleRunner` also listens to bus `weather:change`, which WorldScene emits right after this
 * call with the same target, so both runners agree whichever one is in use (the call is idempotent).
 * Returns the emission the caller broadcasts (t=0 timed, t=1 instant, null when nothing changed).
 */
export function beginWeather(director: WeatherDirector, npcs: WeatherFollower | null, state: WeatherState, transitionMs: number): WeatherEmission | null {
  const em = director.set(state, transitionMs);
  npcs?.setWeather(director.state);
  return em;
}
